import type { College } from '@backrs_ui/common-ui';
import { hasRunningRoi, roiMethodConfigFor } from '@backrs_ui/common-ui';

import { useRegisterAgentContext } from '@backrs_ui/grow-ui/src/agentContext/useRegisterAgentContext';
import { appStrings } from '@backrs_ui/grow-ui/src/assets/appStrings';

const ROI_STRINGS = appStrings.modules.college.returnOnInvestment;

export const useAgentContext = (
  college: College,
  roiData: College['scorecardRoi'],
  nationalMidpoint: number,
  yearsToGraduate: number,
) => {
  useRegisterAgentContext({
    kind: 'section',
    title: 'ROI',
    description:
      'Return on investment data including success rate, median earnings, graduation rate, and ROI over time',
  });

  useRegisterAgentContext({
    kind: 'visual',
    label: 'Success Rate',
    display:
      'Line item showing percentage with a colored high/medium/low chip. Chip is clickable to show level thresholds',
    detail: `${ROI_STRINGS.cards.successRate.tooltip}\n\nLevel thresholds: ${JSON.stringify(ROI_STRINGS.successRateLevels.modal)}`,
    data: {
      value: college.chettyData?.kq5_cond_parq1,
      level: college.chettyData?.success_rate_level,
    },
  });

  useRegisterAgentContext({
    kind: 'visual',
    label: 'Median Earnings',
    display:
      'Line item showing dollar amount with a colored high/medium/low chip. Chip is clickable to show level thresholds',
    detail: `${ROI_STRINGS.cards.medianEarnings.tooltip}\n\nLevel thresholds: ${JSON.stringify(ROI_STRINGS.medianEarningsLevels.modal)}`,
    data: {
      value: college.median_earnings_10yr,
      level: college.median_earnings_level,
    },
  });

  useRegisterAgentContext({
    kind: 'visual',
    label: 'Graduation Rate',
    display:
      'Horizontal percentage bar chart with markers for Pell recipients rate and national midpoint',
    detail: ROI_STRINGS.cards.graduationRate.tooltip(yearsToGraduate),
    data: {
      graduation_rate: college.graduation_rate_8yr,
      pell_recipients: college.graduation_rate_pell_8yr,
      national_midpoint: nationalMidpoint,
    },
  });

  const roiChartStrings = roiData ? (roiMethodConfigFor(roiData)?.strings ?? null) : null;

  useRegisterAgentContext(
    roiData && roiChartStrings
      ? {
          kind: 'visual',
          label: 'ROI Horizon',
          display: hasRunningRoi(roiData)
            ? 'Line chart showing the running ROI for each year from 1 to 10, with a clickable chip showing the 10-year ROI level and a help icon explaining the concept'
            : 'Line chart showing ROI across 10, 15, 20, 30, and 40 year horizons, with a clickable chip showing ROI level and a help icon explaining the concept',
          detail: JSON.stringify({
            roiLevels: roiChartStrings.modalCards,
            roiHorizonExplanation: roiChartStrings.roiHorizonModal,
          }),
          data: {
            roi_method: roiData.roi_method,
            roi_1_year: roiData.roi_1_year,
            roi_2_year: roiData.roi_2_year,
            roi_3_year: roiData.roi_3_year,
            roi_4_year: roiData.roi_4_year,
            roi_5_year: roiData.roi_5_year,
            roi_6_year: roiData.roi_6_year,
            roi_7_year: roiData.roi_7_year,
            roi_8_year: roiData.roi_8_year,
            roi_9_year: roiData.roi_9_year,
            roi_10_year: roiData.roi_10_year,
            roi_15_year: roiData.roi_15_year,
            roi_20_year: roiData.roi_20_year,
            roi_30_year: roiData.roi_30_year,
            roi_40_year: roiData.roi_40_year,
            net_price: roiData.net_price,
            years_enrolled: roiData.years_enrolled,
            cost_total: roiData.cost_total,
          },
        }
      : null,
  );
};
